<template>
    <div class="frame-common" :class="getMainFrameClasses">
        <header class="frame-common__header">
            <div class="frame-common__header__title">
                <router-link :to="{name: 'index'}" class="frame-common__header__title__link">
                    <div class="frame-common__header__title__link__logo">
                        <SemmelweisLogoSvg/>
                    </div>
                    <div class="frame-common__header__title__link__main">{{ $t('labels.pageTitle') }}</div>
                    <div class="frame-common__header__title__link__sub">{{ $t('labels.projectTitle') }}</div>
                </router-link>
            </div>
            <div v-if="$user.isLoggedIn()" class="frame-common__header__user">
                <div class="frame-common__header__user__info">
                    <div class="frame-common__header__user__info__name">{{ $user.data.name }}</div>
                    <div class="frame-common__header__user__info__email">{{ $user.data.email }}</div>
                </div>
                <div class="frame-common__header__user__avatar">
                    <AvatarSvg/>
                </div>
                <div class="frame-common__header__user__logout">
                    <div v-if="$inactivityObserver.running" class="frame-common__header__user__logout__timer">{{ $inactivityObserver.expireAt.formatted }}</div>
                    <router-link v-if="$user.isLoggedIn()" :to="{name: 'logout'}" :title="$t('navigation.authentication.logout')"/>
                </div>
            </div>
        </header>
        <div class="frame-common__content">
            <div class="frame-common__content__navigation">
                <Navigation class="frame-common__content__navigation__menu"/>
                <div class="frame-common__content__navigation__assurance-company" v-if="$user.isLoggedIn()">
                    <div>{{ $t('pages.administration.assuringCompanies.nameShort') }}: {{assuranceCompany.data.name}}</div>
                    <div>{{ $t('pages.administration.assuringCompanies.phone') }}: <a :href="'tel:' + assuranceCompany.data.phone">{{assuranceCompany.data.phone}}</a></div>
                    <div>{{ $t('pages.administration.assuringCompanies.filingCode') }}: {{assuranceCompany.data.filing_code}}</div>
                </div>
            </div>
            <div class="frame-common__content__header" id="frame-common__content__header"></div>
            <router-view class="frame-common__content__view" v-if="!$user.isLoggedIn()" v-slot="{ Component }">
                <Transition>
                    <component :is="Component" :class="getContentViewClasses" class="page"/>
                </Transition>
            </router-view>
            <router-view class="frame-common__content__view page" v-else :class="getContentViewClasses"></router-view>
        </div>
        <footer>
            <Footer/>
        </footer>
        <Moveable v-for="[symbol, item] in timeoutPopups" :key="symbol">
            <div class="timeout-popup">
                <div class="timeout-popup__content">{{item.content}}</div>
                <div class="timeout-popup__cross" @click="item.callback()"></div>
            </div>
        </Moveable>
    </div>
</template>

<script>
import Navigation from "../../components/Navigation.vue";
import Button from "../../components/buttons/Button.vue";
import SemmelweisLogoSvg from "@media/images/vector/sote-logo.svg";
import AvatarSvg from "@media/images/vector/avatar.svg";
import Footer from "../../components/Footer.vue";
import Moveable from "../../components/Moveable.vue";

export default {
    components: {Footer, Button, Navigation, SemmelweisLogoSvg, AvatarSvg, Moveable},

    data() {return {
        timeoutPopups: new Map(),
    };},

    provide() {return {
        addTimeoutPopup: this.addTimeoutPopup.bind(this),
        triggerRequestSentToServerTimeoutPopup: (message) => {
            this.$nextTick(() => { // $nextTick is important here
                this.addTimeoutPopup(message ?? this.$t('labels.requestSentToServer'), 3000);
            })
        }
    };},

    methods: {
        getThemeName() {
            // Or attach a select menu for the theming and return the proper value from that.
            return "-styling-theme-default";
        },

        addTimeoutPopup(string, delay = 0, symbol) {
            if(this.timeoutPopups.has(symbol)) {
                return;
            }

            let item = {
                symbol: symbol ?? Symbol(),
                content: string,
                timeoutId: delay ? setTimeout(() => {
                    item.callback();
                }, delay) : null,
                callback: () => {
                    if(item.timeoutId) {
                        clearTimeout(item.timeoutId);
                    }

                    this.timeoutPopups.delete(item.symbol);
                }
            };

            this.timeoutPopups.set(item.symbol, item);
        }
    },

    computed: {
        getMainFrameClasses() {
            return [
                this.$user.isLoggedIn() ? '-user' : '-guest',
                this.getThemeName()
            ];
        },

        getContentViewClasses() {
            return this.getMainFrameClasses;
        },

        assuranceCompany() {
            return this.$configuration.get('vueJs.actualAssuranceCompanyResource');
        }
    }
}
</script>

<style lang="scss">
@use "../../../scss/mixins";

.frame-common {
    min-height: 100vh;
    display: grid;
    grid-template-rows: auto 1fr auto;

    &__header {
        background-color: var(--header-background-color);
        color: var(--header-font-color);
        padding: var(--padding-thin) var(--padding-thin) var(--padding-thin) var(--padding);

        @include mixins.breakpoint-md {
            padding: var(--padding-thin) var(--padding);
        }

        &__title {
            &__link {
                display: grid;
                grid-template-areas:
                    "logo main"
                    "logo sub";
                grid-template-columns: auto 1fr;
                gap: var(--gap-thin);
                font-weight: bold;
                align-items: center;

                &__logo {
                    grid-area: logo;

                    --logo-dimension: 4.5em;

                    width: var(--logo-dimension);
                    height: var(--logo-dimension);

                    & svg {
                        fill: white;
                    }
                }

                &__main {
                    grid-area: main;
                    font-size: 1.5em;
                }

                &__sub {
                    grid-area: sub;
                    font-size: 0.9em;
                }
            }
        }
    }

    $root: &;

    &.-user {
        #{$root} {
            &__header {
                display: grid;
                grid-template-columns: 1fr auto;
                gap: var(--gap);

                &__user {
                    display: grid;
                    grid-template-columns: 1fr auto auto;
                    gap: var(--gap-thin);
                    align-items: center;

                    &__info {
                        border: {
                            right: 2px solid var(--color-biscuit);
                        }
                        padding: {
                            right: var(--gap-thin);
                        }

                        &__name {

                        }

                        &__email {
                            font-size: 0.8em;

                            @include mixins.breakpoint-md {
                                font-size: 0.9em;
                            }
                        }
                    }

                    &__avatar {
                        height: 2em;
                        display: none;

                        @include mixins.breakpoint-md {
                            display: block;
                        }

                        svg {
                            width: 100%;
                            height: 100%;
                            fill: var(--header-user-fill-color);
                        }
                    }

                    &__logout {
                        &__timer {
                            font-family: monospace;
                        }

                        a:after {
                            --image-dimension-height: 3em;
                            --image-dimension-width: 2em;

                            display: block;
                            content: '';
                            height: var(--image-dimension-height);
                            width: var(--image-dimension-width);
                            background-color: var(--header-user-fill-color);
                            mask: {
                                image: url("@media/images/vector/box-arrow-right.svg");
                                size: contain;
                                repeat: no-repeat;
                                position: center;
                            };
                        }

                        @include mixins.breakpoint-md {
                            margin-left: var(--margin-thick);
                        }
                    }
                }
            }
        }
    }

    &__content {
        display: grid;
        grid-template-rows: auto auto 1fr;

        &__header {
            &:not(:empty) {
                padding: var(--padding-thin);
            }
        }

        &__navigation {
            font-size: 1.2em;
            background-color: var(--navigation-background-color);
            color: var(--navigation-font-color);
            padding: var(--gap-thin);
            @include mixins.box-shadow($top: 3px, $left: 0px, $blur: 5px);
            margin-bottom: var(--margin);
            @include mixins.breakpoint-xl {
                display: grid;
                grid-template-columns: 1fr auto;
            }

            &__assurance-company {
                display: flex;
                gap: var(--gap-thin);
                align-items: center;
                font-size: 0.6em;
                font-weight: bold;
                margin-top: var(--margin-thin);
            }
        }

        &__view {
            padding: var(--padding);
        }
    }
}

.timeout-popup {
    display: grid;
    gap: var(--gap);
    align-items: start;
    grid-template-columns: 1fr 0.9em;

    &__cross {
        $size: 0.9em;

        height: $size;
        width: $size;
        background-color: var(--color-silver);
        mask: {
            position: center;
            repeat: no-repeat;
            size: contain;
            image: url("@media/images/vector/cross-diagonal.svg");
        };
        cursor: pointer;
    }
}
</style>
